/**
 * Password gate.
 *
 * Shown when watch-guard answers `password_required` / `password_incorrect`.
 * It mounts on the app container, NOT on the player — at this point in the boot
 * no player exists: authorize has not returned a source and may never do so.
 *
 * The password is submitted once and never stored. "Remember me" is deliberately
 * absent: the only honest implementation is the session id the server already
 * hands back, which a returning viewer reuses (see ids.ts).
 *
 * Layout is a left-aligned hero rather than a centred card, so a locked stream
 * reads like the title screen it replaces instead of like a login dialog.
 */

const EYE_SVG =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z"/>' +
  '<circle cx="12" cy="12" r="3.2"/></svg>'

const EYE_OFF_SVG =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<path d="M9.9 5.8A9.6 9.6 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a17 17 0 0 1-3.3 4.1"/>' +
  '<path d="M6.3 7.7A17 17 0 0 0 2.5 12S6 18.5 12 18.5c1.5 0 2.8-.3 4-.8"/>' +
  '<path d="M10 10a2.8 2.8 0 0 0 4 4"/><path d="M3.5 3.5l17 17"/></svg>'

const SPINNER_SVG =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" ' +
  'stroke-linecap="round" aria-hidden="true">' +
  '<path d="M12 3a9 9 0 1 0 9 9" opacity="0.9"/></svg>'

const DEFAULT_SUB = 'Enter the password to watch. It is case-sensitive.'

export interface PasswordPromptOptions {
  /** Stream title — branding is the reason denials still carry config */
  title: string
  /** The server's own explanation, when it sent one. Falls back to DEFAULT_SUB. */
  message?: string | null
  /**
   * Optional liveness check. The gate runs before any poller does, so it has no
   * idea whether the stream behind it is actually on air; when this resolves
   * true the eyebrow upgrades from "PRIVATE" to "LIVE NOW · PRIVATE". It is
   * never inferred — an unanswered or false check just leaves the eyebrow alone.
   */
  checkLive?: () => Promise<boolean | null>
  /** Resolve to dismiss, or call setError/setCooldown and stay */
  onSubmit: (password: string) => Promise<void>
}

export interface PasswordPromptHandle {
  /** Show an error under the field and re-select the input */
  setError: (message: string) => void
  /** Lock the form for `seconds` (HTTP 429 → Retry-After) */
  setCooldown: (seconds: number) => void
  /** Fade out and unmount */
  dismiss: () => void
  dispose: () => void
}

export function mountPasswordPrompt(
  host: HTMLElement,
  opts: PasswordPromptOptions,
): PasswordPromptHandle {
  host.querySelector('.lp-password-gate')?.remove()

  const gate = document.createElement('div')
  gate.className = 'lp-password-gate'

  const panel = document.createElement('div')
  panel.className = 'lp-password-panel'

  const eyebrow = document.createElement('div')
  eyebrow.className = 'lp-password-eyebrow'

  const dot = document.createElement('span')
  dot.className = 'lp-password-dot'

  const eyebrowText = document.createElement('span')
  eyebrowText.textContent = 'Private'

  eyebrow.append(dot, eyebrowText)

  const heading = document.createElement('h1')
  heading.className = 'lp-password-title'
  heading.textContent = opts.title || 'Protected stream' // never innerHTML

  const sub = document.createElement('p')
  sub.className = 'lp-password-sub'
  sub.textContent = opts.message || DEFAULT_SUB

  // A real form, so password managers offer to fill and to save. Validation is
  // left ON (no noValidate) so an empty submit gets the browser's own "please
  // fill out this field" callout, in the viewer's language, instead of a
  // silent no-op.
  const form = document.createElement('form')
  form.className = 'lp-password-form'

  const fieldWrap = document.createElement('div')
  fieldWrap.className = 'lp-password-field'

  const field = document.createElement('input')
  field.className = 'lp-password-input'
  field.type = 'password'
  field.name = 'password'
  field.autocomplete = 'current-password'
  field.required = true
  field.placeholder = 'Stream password'
  field.setAttribute('aria-label', 'Stream password')

  // type="button": inside a form, a bare <button> submits, so revealing the
  // password would also fire an attempt.
  const reveal = document.createElement('button')
  reveal.className = 'lp-password-reveal'
  reveal.type = 'button'
  reveal.setAttribute('aria-label', 'Show password')
  reveal.setAttribute('aria-pressed', 'false')
  reveal.innerHTML = EYE_SVG // static markup only

  fieldWrap.append(field, reveal)

  const submit = document.createElement('button')
  submit.className = 'lp-password-submit'
  submit.type = 'submit'

  const spinner = document.createElement('span')
  spinner.className = 'lp-password-spinner'
  spinner.innerHTML = SPINNER_SVG

  const submitLabel = document.createElement('span')
  submitLabel.className = 'lp-password-submit-label'
  submitLabel.textContent = 'Watch'

  submit.append(spinner, submitLabel)

  const error = document.createElement('p')
  error.className = 'lp-password-error'
  error.setAttribute('role', 'status')
  error.setAttribute('aria-live', 'polite')

  form.append(fieldWrap, submit)
  panel.append(eyebrow, heading, sub, form, error)
  gate.appendChild(panel)
  host.appendChild(gate)

  let busy = false
  let cooldownTimer: ReturnType<typeof setInterval> | null = null

  const setBusy = (value: boolean): void => {
    busy = value
    submit.disabled = value
    field.disabled = value
    reveal.disabled = value
    gate.classList.toggle('lp-busy', value)
  }

  const clearCooldown = (): void => {
    if (!cooldownTimer) return
    clearInterval(cooldownTimer)
    cooldownTimer = null
  }

  reveal.addEventListener('click', () => {
    const shown = field.type === 'text'
    field.type = shown ? 'password' : 'text'
    reveal.innerHTML = shown ? EYE_SVG : EYE_OFF_SVG
    reveal.setAttribute('aria-label', shown ? 'Show password' : 'Hide password')
    reveal.setAttribute('aria-pressed', shown ? 'false' : 'true')
    field.focus()
  })

  form.addEventListener('submit', (ev) => {
    ev.preventDefault()
    if (busy || cooldownTimer) return
    // Constraint validation means an empty form never reaches this handler.
    // Kept as a guard for anything that submits the form programmatically.
    const value = field.value
    if (!value) {
      field.reportValidity()
      field.focus()
      return
    }
    error.textContent = ''
    gate.classList.remove('lp-shake')
    setBusy(true)
    void opts
      .onSubmit(value)
      .catch(() => {
        /* the caller reports failure through setError/setCooldown */
      })
      .finally(() => setBusy(false))
  })

  // The stream behind the gate may or may not be on air. Say so only when the
  // answer comes back positive, and never let a failed check change the copy.
  if (opts.checkLive) {
    void opts
      .checkLive()
      .then((live) => {
        if (live !== true || !gate.isConnected) return
        eyebrowText.textContent = 'Live now · Private'
        gate.classList.add('lp-live')
      })
      .catch(() => {
        /* liveness is decoration here — a failure changes nothing */
      })
  }

  // Autofocus only at the top level: inside an iframe it yanks the host page's
  // scroll position to the embed.
  if (window.self === window.top) {
    requestAnimationFrame(() => field.focus())
  }

  return {
    setError(message) {
      error.textContent = message
      // Retrigger the shake even if it is already applied.
      gate.classList.remove('lp-shake')
      void gate.offsetWidth
      gate.classList.add('lp-shake')
      // The caller reports the failure while the form is still disabled — busy
      // is cleared in a `.finally` that runs after this — and a disabled input
      // cannot take focus. Wait for the next task, when the field is live
      // again, or the viewer has to click back into it to retype.
      setTimeout(() => {
        if (field.disabled) return
        field.focus()
        field.select()
      }, 0)
    },

    setCooldown(seconds) {
      clearCooldown()
      let left = Math.max(1, Math.ceil(seconds))
      const render = (): void => {
        const m = Math.floor(left / 60)
        const s = String(left % 60).padStart(2, '0')
        submitLabel.textContent = `Try again in ${m}:${s}`
      }
      field.disabled = true
      submit.disabled = true
      gate.classList.add('lp-cooldown')
      render()
      cooldownTimer = setInterval(() => {
        left--
        if (left <= 0) {
          clearCooldown()
          submitLabel.textContent = 'Watch'
          field.disabled = false
          submit.disabled = false
          gate.classList.remove('lp-cooldown')
          field.focus()
          return
        }
        render()
      }, 1000)
    },

    dismiss() {
      clearCooldown()
      gate.classList.add('lp-leaving')
      setTimeout(() => gate.remove(), 220)
    },

    dispose() {
      clearCooldown()
      gate.remove()
    },
  }
}
